import React, { useState } from 'react';
import { App, Button, Popconfirm, Tag } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';

import ContextBar from '../components/common/ContextBar';
import NotificationList from '../components/notifications/NotificationList';
import FollowingList from '../components/notifications/FollowingList';
import ToggleRow from '../components/common/ToggleRow';
import { usePreferences } from '../contexts/PreferencesContext';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { resumeEmailDelivery } from '../services/emailDelivery';
import { complaintSentence, deliveryOf, resumeLockedUntil } from '../utils/emailDelivery';
import { formatDisplayDate } from '../utils/dateDisplay';
import { NOTIFICATION_CHANNELS, describeChannelReason, resolveChannels } from '../config/notificationChannels';
import { setMasterChannel } from '../services/notifications';
import { apiErrorMessage } from '../utils/apiErrors';

/**
 * /account/notifications — everything that can reach this person, from every
 * collection they are in, and the three masters above it.
 *
 * The masters are not a fourth tier of the cascade. They are the account's
 * answer to "does keepr email me at all", which is a different question from
 * "does this notification email me": one is the wire, the others are the
 * messages on it. `notificationEmails` has existed in `userPreferences` since
 * the notify action shipped and had no UI anywhere until this page gave it
 * one; it is now RETIRING — the switch writes the `notificationSettings`
 * overlay at the global-per-user tier (`setMasterChannel`, which names the
 * API-side migration that finishes the job) and the preference is kept
 * written, and read, for one more release.
 *
 * Text and Push are off and disabled, with the reason beside them rather than
 * on them: a bare disabled switch says "no" without saying why. ⚠ They are
 * drawn that way rather than driven off `channel.available` on purpose — a
 * live switch needs a value to show, and there is no READ for a master (the
 * settings routes are a PUT and a DELETE; the catalog carries availability,
 * not the master's stored answer), so an enabled switch here would read ON for
 * a channel somebody had turned off. When sms ships, this page needs that read
 * as much as it needs the flag — see `ASSUMED_AVAILABILITY`.
 *
 * Below the rules, **Items you follow** (FollowingList, KPR-391): every item
 * the person follows, with Unfollow, and whether those changes are mailed.
 *
 * `?find=<key or name>` arrives from the inbox drawer's "Mute this
 * notification…": the thing shouting at you hands you the door to its own
 * settings, and the row it names flashes so the door opens on the right row.
 *
 * AFTER A SPAM REPORT (plan §3.5). When this person reported a keepr mail as
 * spam, the API turned the Email master off and stopped mailing the address
 * (`emailDelivery.state === 'complained'`). A bare off switch would read as
 * something they did here, so the row says why, and the switch becomes
 * **Resume emails** behind an inline confirm — the one legitimate way back,
 * because turning mail on again is an explicit re-opt-in (`POST
 * /api/email-delivery/resume`: releases their own complaint stop and turns the
 * master on in both stores). After a SECOND report the API locks resume for 30
 * days; the row says until when.
 */
export default function AccountNotifications() {
	const { prefs, setPref, loading, ensureLoaded } = usePreferences();
	const { user, refresh } = useCurrentUser();
	const { message } = App.useApp();
	const delivery = deliveryOf(user);
	const complained = delivery && delivery.state === 'complained' ? delivery : null;
	const lockedUntil = resumeLockedUntil(complained);
	const [resuming, setResuming] = useState(false);

	const resume = async () => {
		setResuming(true);
		try {
			await resumeEmailDelivery();
			message.success(`Emails to ${complained.address || user.email} will start again.`);
			// Both stores changed on the server: re-read the account (its
			// delivery state) and the preferences (the legacy master).
			await Promise.all([refresh(), ensureLoaded({ force: true })]);
		} catch (error) {
			message.error(apiErrorMessage(error, 'Emails could not be resumed. Please try again.'));
		} finally {
			setResuming(false);
		}
	};
	const [ searchParams ] = useSearchParams();
	const find = searchParams.get('find') || '';

	// The switch moves on click, not on the round trip — the platform's
	// settings idiom — so the master carries its own optimistic value until
	// the stores agree again.
	const [pendingEmail, setPendingEmail] = useState(null);
	const emailOn = pendingEmail === null ? prefs.notificationEmails !== false : pendingEmail;

	/**
	 * Two stores, in the order that keeps them honest: the settings row FIRST,
	 * because the API's email channel module reads it first and a failure
	 * there must not leave the preference claiming something the delivery path
	 * disagrees with. The preference write behind it is the transition's other
	 * half and goes when `retireNotificationEmails` runs.
	 */
	const writeEmailMaster = async (value) => {
		setPendingEmail(value);
		try {
			await setMasterChannel('email', value);
		}
		catch (error) {
			setPendingEmail(null);
			message.error(apiErrorMessage(error, 'That change did not save. Please try again.'));
			return;
		}
		await setPref('notificationEmails', value);
		setPendingEmail(null);
	};

	// The registry, asked what it knows without a catalog to ask the API with:
	// email delivers, the other two are plumbed and waiting.
	const channels = resolveChannels(null);
	const masterFor = key => channels.find(channel => channel.key === key)
		|| NOTIFICATION_CHANNELS.find(channel => channel.key === key);

	const email = masterFor('email');
	const sms = masterFor('sms');
	const push = masterFor('push');

	return (
		<>
			{/* It names no record, so there is no chip: a plain title under
			    the one breadcrumb segment above it. */}
			<ContextBar
				breadcrumb={[{ label: 'Account', to: '/account' }]}
				title="Notifications"
			/>

			<div className="settings-column notifications-page">
				<section className="form-panel">
					<div className="micro-label form-panel__kicker">How keepr reaches you</div>
					<p className="form-panel__hint">
						The masters. Turning one off here stops that channel for every notification
						below, whatever each row says. Your inbox is not one of them — every
						notification is written there either way.
					</p>

					<ToggleRow
						first
						label={email.label}
						description={complained
							? `Off because ${complaintSentence(complained, user && user.email, { lower: true })}.${lockedUntil ? ` You can resume them on ${formatDisplayDate(lockedUntil)}.` : ''}`
							: 'Notification emails from every collection you are in.'}
						checked={emailOn}
						disabled={loading}
						onChange={writeEmailMaster}
					>
						{complained ? (
							<Popconfirm
								title="Resume emails?"
								description={`Emails to ${complained.address || user.email} will start again.`}
								okText="Resume emails"
								cancelText="Cancel"
								onConfirm={resume}
								disabled={Boolean(lockedUntil)}
							>
								<Button loading={resuming} disabled={Boolean(lockedUntil)}>Resume emails</Button>
							</Popconfirm>
						) : undefined}
					</ToggleRow>
					{/* Plumbed, not decorative: the rule below can already be
					    authored to use them, and the day the channel ships
					    these switches start working with no re-authoring. */}
					<ToggleRow
						label={<>{sms.label} messages <Tag className="notification-tag">Coming soon</Tag></>}
						aria-label={`${sms.label} messages`}
						description={describeChannelReason(sms.reason || 'channel_not_built')}
						checked={false}
						disabled
					/>
					<ToggleRow
						label={<>{push.label} <Tag className="notification-tag">Coming soon</Tag></>}
						aria-label={push.label}
						description={describeChannelReason(push.reason || 'channel_not_built')}
						checked={false}
						disabled
					/>
				</section>

				<NotificationList
					tier="user"
					find={find}
					emptyText="Nothing notifies you yet."
					emptyAction={<Link to="/collections">Open a collection</Link>}
				/>

				{/* The items this person follows (KPR-391): a different thing from
				    the rules above — nobody authored these, the person asked —
				    so its own panel, with its own email switch. */}
				<FollowingList />
			</div>
		</>
	);
}
